import React, { useRef, useState, useEffect } from 'react';
import { Dialog, DialogContent, Grid, Link } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { GradientColorHeader } from '@Local/Components/Dialog/';
import { Alert, Button, Collapse, Form, TextField } from '@curves/ui-base';

import { localUser } from '@Util/utils';
import useAuthApi from '@Apis/useAuthApi';
import { HQ_ROOT_ROUTER } from '@Router/routerPath';
// import '../mockjs/MockJs';
import LoginHeader_1 from '@Images/login_1.svg';
import LoginHeader_2 from '@Images/login_2.svg';
import LoginLogo from '@Images/logo-white.svg';
import LoginFooterBg from '@Images/footer-bg.svg';
import { generateSecurity } from '@Util/apiCryto';
import useGoogleSignIn from './useGoogleSignIn';
const entrancePathRoot = {
    1: HQ_ROOT_ROUTER,
};
const MessageDialog = (props) => {
    const { open = false, text: textProps, onClose: onCloseProps } = props;
    const handleClose = (e) => {
        onCloseProps(e, false);
    };
    return (
        <Dialog open={open} fullWidth>
            <GradientColorHeader>訊息</GradientColorHeader>
            <DialogContent dividers>
                <Grid container>
                    <Grid item xs={12}>
                        <Alert variant="outlined" severity="error" color="error">
                            {textProps}
                        </Alert>
                    </Grid>
                    <Grid item xs={12} className="flex flex-y-flex-center my-2">
                        <Button size="large" variant="contained" color="secondary" onClick={handleClose}>
                            確定
                        </Button>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};
// 登入頁面
const LoginPage = () => {
    const history = useHistory();
    const { enqueueSnackbar: _snackbar } = useSnackbar();
    const { postLoginApi, postGoogleLoginApi, getGoogleLoginConfigApi, getLogoutApi, getCheckLocalDataApi, getLocalMeDataApi } = useAuthApi();
    const [isShowDialog, setShowDialog] = useState({
        open: false,
        text: null,
    });
    // Google 登入設定，isLoaded 為 false 時尚未取得，避免登入區塊閃爍
    const [googleSignIn, setGoogleSignIn] = useState({ isLoaded: false, clientId: '' });
    // 帳號密碼登入區塊是否已展開
    const [isCredentialFormOpen, setCredentialFormOpen] = useState(false);
    const usernameRef = useRef(null);
    const passwordRef = useRef(null);
    const isGoogleEnabled = googleSignIn.isLoaded && Boolean(googleSignIn.clientId);
    // Google 登入未啟用時已無其他登入方式，帳號密碼區塊直接展開
    const isCredentialFormVisible = googleSignIn.isLoaded && (!isGoogleEnabled || isCredentialFormOpen);
    const handleShowDialog = (e, isOpen) => {
        setShowDialog((pre) => ({ ...pre, open: isOpen }));
    };
    const showErrorDialog = (text) => {
        setShowDialog((pre) => ({ ...pre, open: true, text }));
    };
    const handleOpenCredentialForm = () => {
        setCredentialFormOpen(true);
    };
    const handleLoginApi = (e) => {
        e.preventDefault();
        e.stopPropagation();
        getResult();
    };
    const getResult = () => {
        if (!usernameRef.current.isError() && !passwordRef.current.isError()) {
            const usr = usernameRef.current.getResult();
            const pwd = passwordRef.current.getResult();
            doLoginApi(usr, pwd);
        }
    };
    const doLoginApi = async (usr, pwd) => {
        const { hmacSecurity, hmacKey } = generateSecurity(pwd);
        await postLoginApi(
            { username: usr, password: hmacSecurity, hmacKey },
            {
                callbackfn: (oriData, apiData) => {
                    const { data } = apiData;
                    if (!data.success) {
                        setShowDialog((pre) => ({
                            ...pre,
                            open: true,
                            text: apiData.data.message,
                        }));
                    } else {
                        loginAction(data.result);
                    }
                },
            },
        );
    };
    // Google 驗證通過後，將 ID Token 交由後端驗簽並換取本站登入狀態
    const doGoogleLoginApi = async (credential) => {
        await postGoogleLoginApi(
            { credential },
            {
                callbackfn: (oriData, apiData) => {
                    const { data } = apiData;
                    if (!data.success) {
                        showErrorDialog(data.message);
                    } else {
                        loginAction(data.result);
                    }
                },
            },
        );
    };
    const googleButtonRef = useGoogleSignIn({
        clientId: googleSignIn.clientId,
        onCredential: doGoogleLoginApi,
        onError: showErrorDialog,
    });
    const loginAction = async (_result) => {
        const { routerType, ...others } = _result;
        const firstPath = entrancePathRoot[routerType];
        if (firstPath) {
            localUser.set({ ...others });
            history.push(entrancePathRoot[routerType]);
        } else {
            _snackbar('權限錯誤,請通知IT人員協助排解', { variant: 'error' });
            await getLogoutApi();
        }
    };
    const didLoginRef = useRef(false);
    useEffect(
        () => {
            if (didLoginRef.current) return;
            didLoginRef.current = true;
            const fetchData = async () => {
                let resp = await getCheckLocalDataApi();
                if (resp) {
                    resp = await getLocalMeDataApi();
                    resp && loginAction(resp);
                    return;
                }
                const googleSetting = await getGoogleLoginConfigApi();
                setGoogleSignIn({
                    isLoaded: true,
                    clientId: googleSetting && googleSetting.isEnabled ? googleSetting.clientId : '',
                });
            };
            fetchData();
        },
        // eslint-disable-next-line
        [],
    );
    return (
        <React.Fragment>
            <div className="login-header">
                <div className="login-header-container">
                    <div className="login-header-bg">
                        <img src={LoginHeader_1} className="login-header-1" alt="curves" />
                        <img src={LoginHeader_2} className="login-header-2" alt="curves" />
                        <img src={LoginLogo} className="login-logo" alt="logo" />
                    </div>
                </div>
            </div>
            <div className="login-body">
                <div className="login-body-container">
                    {isGoogleEnabled && (
                        <div className="login-google">
                            {/* 按鈕由 Google Identity Services 繪製於此容器內 */}
                            <div className="login-google-btn" ref={googleButtonRef} />
                            <div className="login-divider">
                                <span>或</span>
                            </div>
                            {!isCredentialFormOpen && (
                                <div className="login-credential-toggle">
                                    <Link component="button" type="button" underline="always" onClick={handleOpenCredentialForm}>
                                        總部人員帳密登入
                                    </Link>
                                </div>
                            )}
                        </div>
                    )}
                    <Collapse in={isCredentialFormVisible} unmountOnExit>
                        <Form className="login-form" onSubmit={handleLoginApi}>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField ref={usernameRef} label="帳號" helperText="帳號不得為空值" fullWidth required />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField ref={passwordRef} label="密碼" helperText="密碼不得為空值" type="password" fullWidth required />
                                </Grid>
                            </Grid>
                            <div className="login-btn-confirm">
                                <Button variant="outlined" type="submit" color="primary">
                                    登入
                                </Button>
                            </div>
                        </Form>
                    </Collapse>
                </div>
            </div>
            <div className="login-footer">
                <img src={LoginFooterBg} alt="FooterBackground" />
                <div className="login-copyright ban-select">
                    <span>版權所有 © 2024 Curves 可爾姿女性30分鐘環狀運動</span>
                </div>
            </div>
            <MessageDialog open={isShowDialog.open} text={isShowDialog.text} onClose={handleShowDialog} />
        </React.Fragment>
    );
};
export default LoginPage;
